import WRAP from "WRAP-JS";
import {
  configMetarTafDh,
  configMetarTafLayerMetar,
  configMetarTafLayerTaf
} from "./config/ConfigMetarTaf";
import { formatTimeZoneDate, translation, convertWRAPPoint, deepCopy, formatUTCDate } from "../../../utils/FPEUtils";
// import LegendMetarTaf from "../legend/LegendMetarTaf";
import { WX_ID_METARTAF } from "../../../store/WeatherContentsState";
import reactDom from "react-dom";
import CustomDraggable from "../../../parts/CustomDraggable";
import clsx from "clsx";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_METAR_TAF_LATEST_DATA, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";
import { IconButton, Tooltip } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { CONTENTS_ID_POINT_SUMMARY } from "../../../store/DisabledContentsState";

class MetarTaf {
  static NAME = WX_ID_METARTAF;
  static METAR_NAME = 'METAR';
  static TAF_NAME = 'TAF';
  static PROVIDER_PRIORITY = ['JMA', 'FAA', 'NOA'];

  MIN_SIZE_X = 100;
  MIN_SIZE_Y = 100;
  OFFSET_X = 5;
  airportGroupList;
  airportGroup;
  BASE_Z_INDEX = 10001; // WRAPのtooltipが10000なのでそれより上位にくる

  contentsVisibleState = {
    [MetarTaf.METAR_NAME]: true,
    [MetarTaf.TAF_NAME]: true
  };

  baseTime = {
    [MetarTaf.METAR_NAME]: '---',
    [MetarTaf.TAF_NAME]: '---'
  }

  dh;
  metarDh;
  tafDh;
  layerMetar;
  layerTaf;
  ready = false;
  dispatcher;
  visible;
  contentsVisibleCount = this.getContentsVisibleCount();
  metarTafWindows = {};
  saveData = {};
  autoUpdate = true;
  errorMessageInfo;

  greenReg = /^TEMPO$|^BECMG$|^RMK$|^FM.*|PROB.*/;
  redReg =
    /TS|FZ|FG|FU|VA|DU|SA|HZ|SN|SG|PL|GR|GS|IC|PO|SQ|FC|SS|DS|WS|TURB|^0[0-9][0|5]0$|BKN00[0-3]|OVC00[0-3]|VV00[0-3]|G(3[5-9]|[4-9][0-9])KT=?$|G(1[8-9]|[2-9][0-9])MPS=?$|^1\/16SM$|^M1\/4SM$/;
  ktRedReg = /(.*0)((2[3-9]|[3-9][0-9])KT=?)$/;
  mpsRedReg = /(.*0)((1[2-9]|[2-9][0-9])MPS=?)$/;
  smIfPrev1SkipRedReg = /^1\/8SM$|^3\/8SM$/;
  smIfPrev1Or2SkipRedReg = /^1\/2SM$|^1\/4SM$/;
  greenStyle = { color: 'rgb(0 128 0)', fontWeight: 700 };
  redStyle = { color: 'rgb(239 68 68)', fontWeight: 700 };

  constructor(disabled) {
    this.layerMetar = new WRAP.Geo.Layer(MetarTaf.METAR_NAME);
    this.layerTaf = new WRAP.Geo.Layer(MetarTaf.TAF_NAME);
    this.layerMetar.setVisible(false);
    this.layerTaf.setVisible(false);
    this.pointSummaryDisabled = disabled[CONTENTS_ID_POINT_SUMMARY]
    this.layerMetar.setRespondable(true);
    this.layerTaf.setRespondable(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD);
  }

  setAirportGroupList(airportGroupList) {
    this.airportGroupList = airportGroupList;
    if (this.airportGroup === undefined || !this.airportGroupList.includes(this.airportGroup)) {
      this.setAirportGroup(this.airportGroupList.length > 0 ? this.airportGroupList[0] : '');
    }
  }

  reflectChangeAirportGroup(change) {
    if (change.target === this.airportGroup) {
      this.setAirportGroup(this.airportGroup);
    }
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initHandler();
    this.initWindow();
    this.ready = true;
  }

  _updateMetarTafDh(data) {
    if (data.features !== undefined) {
      const metarFeatures = [];
      const tafFeatures = [];

      data.features.forEach(feature => {
        if (feature.properties.METAR) {
          const metar = deepCopy(feature);
          delete metar.properties.TAF;
          metarFeatures.push(metar);
        }
        if (feature.properties.TAF) {
          const taf = deepCopy(feature);
          delete taf.properties.METAR;
          tafFeatures.push(taf);
        }
      })

      const metar = {
        type: 'FeatureCollection',
        features: metarFeatures
      };
      const taf = {
        type: 'FeatureCollection',
        features: tafFeatures
      }
      this.metarDh.update('data', metar);
      this.tafDh.update('data', taf);
    }
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configMetarTafDh);
      this.metarDh = new WRAP.Data(configMetarTafDh);
      this.metarDh.cancel('data', true)
      this.tafDh = new WRAP.Data(configMetarTafDh);
      this.tafDh.cancel('data', true)
      this._updateDataset();
      this.missingDataObserver.monitoring(MetarTaf.METAR_NAME, this.dh, this.layerMetar, {makeTimelist: this._makeTimelist.bind(this), interactLayerVisible: true});
      this.missingDataObserver.monitoring(MetarTaf.TAF_NAME, this.dh, this.layerTaf, {makeTimelist: this._makeTimelist.bind(this), interactLayerVisible: true});

      this.dh.updated('data', ref => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        this._updateMetarTafDh(ref)
        this._setBaseTime(ref);
        this._updateMetarTafWindow();
        resolve(ref);
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
            message: COMMUNICATE_ERROR_MESSAGE_METAR_TAF_LATEST_DATA,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });
    });
  }

  initLayer() {
    this.layerMetar.configure(this.metarDh, configMetarTafLayerMetar);
    // this.layerMetarAuto.configure(this.dh, configMetarTafLayerMetarAuto);
    this.layerTaf.configure(this.tafDh, configMetarTafLayerTaf);
  }

  initHandler() {
    WRAP.Geo.addEventHandler('mouseover', this.mouseOverHandler.bind(this));
    WRAP.Geo.addEventHandler('mouseout', this.mouseOutHandler.bind(this));
    WRAP.Geo.addEventHandler('touch', this.touchHandler.bind(this));
  }

  initWindow() {
    this.mapContainer = document.getElementById('map-container');
    Object.keys(this.saveData).forEach(k => {
      if (!this.dh.retained.data.features.find(f => f.properties.icao_code === k)) {
        delete this.saveData[k];
      } else {
        const data = this.saveData[k];
        this.drawMetarTafWindow(k, data, true);
      }
    });
    this.setZIndexStyle();
  }

  _getMetarTafText(icao) {

    const airportData = this.dh.retained.data.features.find(feature => feature.properties.icao_code === icao);
    const airportProperties = airportData && airportData.properties ? airportData.properties : {};

    const metarTafText = [];
    Object.keys(this.contentsVisibleState).forEach(contents => {
      if (this.contentsVisibleState[contents]) {
        const metarTafData = airportProperties[contents];
        if (!metarTafData) return;
        for (let provider of MetarTaf.PROVIDER_PRIORITY) {
          const bulletin = metarTafData[provider]?.bulletin;
          if (bulletin) {
            metarTafText.push({
              type: contents,
              title: `${contents}:`,
              bulletin: bulletin
            });
            return;
          }
        }
      }
    });
    return metarTafText;
  }

  _updateMetarTafWindow = () => {
    Object.keys(this.metarTafWindows).forEach(key => {
      const window = this.metarTafWindows[key];
      const x = window.style.left.replace('px', '')-0;
      const y = window.style.top.replace('px', '')-0;
      this._createWindow(key, {x,y}, window);
    });
    this._showAllWindow();
  }

  drawMetarTafWindow(icao, data, initWindow) {
    if (this.metarTafWindows[icao]) return;
    if(!this.windowsElem) {
      this.windowsElem = document.createElement('div');
      this.mapContainer.appendChild(this.windowsElem);
    }
    const metarTafWindow = document.createElement('div');
    metarTafWindow.setAttribute('class', 'metar-taf-window');
    this.windowsElem.appendChild(metarTafWindow);
    this._createWindow(icao, data, metarTafWindow);
    this.metarTafWindows[icao] = metarTafWindow;

    this._positioningTooltip(data, metarTafWindow, icao, initWindow);
  }

  _createWindow(icao, data, target) {
    const metarTafText = this._getMetarTafText(icao);
    reactDom.render(
      <CustomDraggable
        handle=".metar-taf-tooltip"
        onStart={this.dragStartHandler.bind(this)}
        onStop={this.dragStopHandler.bind(this)}
        baseX={data.x}
        baseY={data.y}
        parent={this.mapContainer}
        borderOffset={{right: -50}}
        bounce={{right: 80}}
      >
        <div className={clsx("metar-taf-tooltip", metarTafText.length === 0 && "metar-taf-tooltip-min")} data-icao={icao} onClick={this.windowClickHandler.bind(this)}>
          <div className="metar-taf-header">
            {this.pointSummaryDisabled ? <div>{icao}</div> : <div className="metar-taf-header-icao" onClick={this._clickIcaoCode}>{icao}</div>}
          </div>
          <div className="metar-taf-window-close" data-icao={icao} onClick={this._closeWindow}>×</div>
          <div>
            {
              metarTafText.map((d, i) => {
                const parsedBulletin = this.parseBulletin(d.bulletin);
                return <div key={i} className="metar-taf-text">
                  {i !== 0 && <div className="metar-taf-text-divider"></div>}
                  <div className={clsx(d.type === MetarTaf.METAR_NAME ? "metar-header" : "taf-header")}>
                    {d.title}
                    <IconButton aria-label="copy" size="small" onClick={() => this._copyClipboard(parsedBulletin)} style={{padding: 0, marginLeft: '5px'}}>
                      <Tooltip title={`copy ${d.type}`} arrow>
                        <FileCopyIcon fontSize="small" style={{ fontSize: '1rem'}}/>
                      </Tooltip>
                    </IconButton>
                  </div>
                  <div className="metar-taf-text-bulletin">{parsedBulletin.map((line, j) => {
                    return <div key={j} >{line.map((v, k) => {
                      return <span key={k} style={v.style || {}}>{v.noSpace ? v.str : ` ${v.str}`}</span>
                    })}</div>
                  })}</div>
                </div>
              })
            }
          </div>
        </div>
      </CustomDraggable>,
      target
    )
  }

  _removeWindow(icao) {
    if (!this.windowsElem) return;

    const target = this.metarTafWindows[icao];
    if (!target) return;

    // Reactコンポーネントをアンマウント
    reactDom.unmountComponentAtNode(target);

    // DOM要素自体を削除
    target.remove();

    // 管理用オブジェクトからも削除
    delete this.metarTafWindows[icao];
    delete this.saveData[icao];

    // 子ウィンドウが全て無くなったら親divも削除
    if (this.windowsElem && this.windowsElem.childElementCount === 0) {
      this.windowsElem.remove();
      this.windowsElem = null;
    }
  }

  _clearWindow() {
    Object.keys(this.metarTafWindows).forEach(icao=> {
      this._removeWindow(icao);
    })
  }

  _copyClipboard = (bulletin) => {
    if (navigator.clipboard) {
      let text;
      if (Array.isArray(bulletin)) {
        text = bulletin.map(
          line => line.map(v => v.noSpace ? v.str : ` ${v.str}`).join('').trimStart()
        ).join('\r\n')
      } else {
        text = bulletin;
      }
      navigator.clipboard.writeText(text).then(() => {
        this.dispatcher({type: 'SHOW_TIPS', payload: 'copied to clipboard'});
      });
    }
  }

  _positioningTooltip = (data, target, icao, initWindow) => {
    const mapH = this.mapContainer.clientHeight;
    const mapW = this.mapContainer.clientWidth;
    const tooltipH = target.clientHeight ? target.clientHeight : target.firstChild.clientHeight;
    const tooltipW = target.clientWidth ? target.clientWidth : target.firstChild.clientWidth;

    let top = data.y;
    let left = data.x;

    if (!initWindow) {
      // 縦 下が切れる場合
      if (mapH < tooltipH + data.y) {
        top = data.y - tooltipH;
      }
      // 横 右が切れる場合
      left += this.OFFSET_X;
      if (mapW < tooltipW + data.x) {
        left = data.x - tooltipW - this.OFFSET_X * 2;
      }
    } else {
      // 下
      if (mapH < top + this.MIN_SIZE_Y) {
        top = mapH - this.MIN_SIZE_Y;
      }
      // 上
      else if (top + tooltipH < this.MIN_SIZE_Y) {
        top = -tooltipH + this.MIN_SIZE_Y;
      }
      // 右
      if (mapW < left + this.MIN_SIZE_X) {
        left = mapW - this.MIN_SIZE_X;
      }
      // 左
      else if (left + tooltipW < this.MIN_SIZE_X) {
        left = -tooltipW + this.MIN_SIZE_X;
      }
    }

    target.style.top = `${top}px`;
    target.style.left = `${left}px`;
    if (icao) {
      this.saveData[icao] = {x: left, y: top, originalX: left, originalY: top, zIndex: data.zIndex};
      if (!initWindow) {
        this.saveData[icao].zIndex = Object.keys(this.saveData).length + 1 + this.BASE_Z_INDEX;
      }
      this.setZIndexStyle();
    }
  }

  dragStartHandler(event, data) {
    const icao = data.node.dataset.icao;
    this.metarTafWindows[icao].style.zIndex = Object.keys(this.metarTafWindows).length + 1 + this.BASE_Z_INDEX;
  }

  dragStopHandler(event, data) {
    const icao = data.node.dataset.icao;
    const d = this.saveData[icao];
    d.x = d.originalX + data.x;
    d.y = d.originalY + data.y;
    this.updateZIndex(icao);
  }

  windowClickHandler(event) {
    const icao = event.currentTarget.dataset.icao;
    if (this._findWindow(icao)) {
      this.updateZIndex(icao);
    }
  }

  updateZIndex(icao) {
    const saveDataArray = Object.keys(this.saveData);
    this.saveData[icao].zIndex = saveDataArray.length + 1 + this.BASE_Z_INDEX;
    Object.keys(this.saveData).map(d => this.saveData[d]).sort((a, b) => a.zIndex < b.zIndex ? -1 : 1).forEach((d, i) => d.zIndex = i + 1 + this.BASE_Z_INDEX);
    this.setZIndexStyle();
  }

  setZIndexStyle() {
    Object.keys(this.metarTafWindows).forEach(d => {
      this.metarTafWindows[d].style.zIndex = this.saveData[d].zIndex;
    });
  }

  parseBulletin(origBulletin) {
    const bulletin = []
    const bulletinLines = origBulletin.split(/[\r\n]/g);
    bulletinLines.forEach((line, i) => {
      const splitted = line.split(' ');
      const bulletinLine = [];
      splitted.forEach((v, j) => {
        let m = null;
        v.trim();
        if (v !== '') {
          if (i === 0 && j <= 1) {
            bulletinLine.push({ str: v });
          } else if (this.greenReg.test(v)) {
            bulletinLine.push({
              style: this.greenStyle,
              str: v,
            });
          } else if (this.redReg.test(v)) {
            bulletinLine.push({
              style: this.redStyle,
              str: v,
            });
          } else if ((m = v.match(this.ktRedReg)) !== null) {
            bulletinLine.push({ str: m[1] });
            bulletinLine.push({
              style: this.redStyle,
              str: m[2],
              noSpace: true,
            });
          } else if ((m = v.match(this.mpsRedReg)) !== null) {
            bulletinLine.push({ str: m[1] });
            bulletinLine.push({
              style: this.redStyle,
              str: m[2],
              noSpace: true,
            });
          } else if (this.smIfPrev1SkipRedReg.test(v)) {
            if (splitted[j - 1] === '1') {
              bulletinLine.push({ str: v });
            } else {
              bulletinLine.push({
                style: this.redStyle,
                str: v,
              });
            }
          } else if (this.smIfPrev1Or2SkipRedReg.test(v)) {
            if (splitted[j - 1] === '1' || splitted[j - 1] === '2') {
              bulletinLine.push({ str: v });
            } else {
              bulletinLine.push({
                style: this.redStyle,
                str: v,
              });
            }
          } else if (v === 'AUTO') {
            bulletinLine.push({ style: { fontWeight: 700 }, str: v });
          } else {
            bulletinLine.push({ str: v });
          }
        }
      });
      bulletin.push(bulletinLine);
    })
    return bulletin;
  }

  mouseOverHandler(layer, feature) {
    if (layer && (layer.name() === MetarTaf.METAR_NAME || layer.name() === MetarTaf.TAF_NAME)) {
      const prop = feature.geo.properties;
      if (this._findWindow(prop.icao_code)) {
        return;
      }

      if(!this.tooltip) {
        this.tooltip = document.createElement('div');
        this.tooltip.setAttribute('class', 'metar-taf-tooltip');
        this.mapContainer.appendChild(this.tooltip);
      }
      const metarTafText = this._getMetarTafText(prop.icao_code);
      if (metarTafText.length === 0) {
        this.tooltip.classList.add('metar-taf-tooltip-min');
      } else {
        this.tooltip.classList.remove('metar-taf-tooltip-min');
      }
      reactDom.render(
        <>
          <div className="metar-taf-header">{prop.icao_code}</div>
          <div>
            {
              metarTafText.map((d, i) => {
                return <div key={i} className="metar-taf-text">
                  {i !== 0 && <div className="metar-taf-text-divider"></div>}
                  <div className={clsx(d.type === MetarTaf.METAR_NAME ? "metar-header" : "taf-header")}>{d.title}</div>
                  <div className="metar-taf-text-bulletin">{this.parseBulletin(d.bulletin).map((line, j) => {
                    return <div key={j} >{line.map((v, k) => {
                      return <span key={k} style={v.style || {}}>{v.noSpace ? v.str : ` ${v.str}`}</span>
                    })}</div>
                  })}</div>
                </div>
              })
            }
          </div>
        </>,
        this.tooltip
      )
      this.tooltip.style.zIndex = Object.keys(this.metarTafWindows).length + 1 + this.BASE_Z_INDEX;
      const coordinates = feature.geo.geometry.coordinates;
      const sPoint = WRAP.Geo.getScreenPoint(convertWRAPPoint(coordinates[1], coordinates[0]));
      this.tooltip.style.display = "block";
      this._positioningTooltip({x:sPoint.x, y:sPoint.y}, this.tooltip);
    }
  }

  mouseOutHandler(layer, feature) {
    if (layer && (layer.name() === MetarTaf.METAR_NAME || layer.name() === MetarTaf.TAF_NAME)) {
      this._removeTooltip();
    }
  }

  touchHandler(layer, feature) {
    if (layer && (layer.name() === MetarTaf.METAR_NAME || layer.name() === MetarTaf.TAF_NAME)) {
      this._removeTooltip();
      const prop = feature.geo.properties;
      if (this._findWindow(prop.icao_code)) {
        return;
      }

      const coordinates = feature.geo.geometry.coordinates;
      const sPoint = WRAP.Geo.getScreenPoint(convertWRAPPoint(coordinates[1], coordinates[0]));

      this.drawMetarTafWindow(prop.icao_code, {x:sPoint.x, y:sPoint.y});
    }
  }

  _removeTooltip = () => {
    if (this.tooltip) {
      this.tooltip.style.display = "none";
    }
  }

  _findWindow = (icao) => {
    let ret = false;
    if (this.metarTafWindows[icao]) {
      ret = true;
    }
    return ret;
  }

  _closeWindow = (event) => {
    const icao = event.target.dataset.icao;
    this._removeWindow(icao);
  }
  //
  // _removeAllWindow = () => {
  //   if (!this.windowsElem) return;
  //   this.windowsElem.remove();
  //   this.windowsElem = null;
  //   this.metarTafWindows = {};
  //   this.saveData = {};
  // }

  _hideAllWindow = () => {
    if (!this.windowsElem) return;
    this.windowsElem.style.display = "none";
  }

  _showAllWindow = () => {
    if (!this.windowsElem) return;
    this.windowsElem.style.display ="block";
  }

  _changeWindowVisible = (visible) => {
    const allLayers = this.getLayer();
    if (0 <= allLayers.map(l => l.visible()).indexOf(true)) {
      if (!visible) {
        this._updateMetarTafWindow();
      }
    } else {
      this._hideAllWindow();
    }
  }

  getExtendedOperation() {
    if (!this.ready) return;
    return {
      window: {
        function: "setInitWindows",
        value: this.saveData
      }
    }
  }

  setInitWindows(windows) {
    this._clearWindow();
    this.saveData = deepCopy(windows);
    if (this.ready) {
      this.initWindow();
    }
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, contents) {
    if (contents) {
      this.contentsVisibleState[contents] = visible;
    }
    if (!this.ready) return;

    let targetLayer;
    switch (contents) {
      case MetarTaf.METAR_NAME:
        targetLayer = [this.layerMetar];
        break;
      case MetarTaf.TAF_NAME:
        targetLayer = [this.layerTaf];
        break;
      default:
        targetLayer = this._allLayers()
    }

    const visibleCount = this.getContentsVisibleCount();

    if (visible && this.visible) {
      targetLayer.forEach(layer => {
        const layerName = layer.name();
        if (this.contentsVisibleState[layerName]) {
          layer.show();
        }
      });
    } else {
      targetLayer.forEach(layer => {layer.hide()});
    }

    if (contents !== undefined && visibleCount > 0) {
      this._updateDataset();
      if (visible && this.visible) {
        this.dh.load('data', null, true);
      }
    }
    this.contentsVisibleCount = visibleCount;
    if (this.autoUpdate !== this.visible) {
      this.dh.setAutoUpdate('data', this.visible);
      this.autoUpdate = this.visible;
    }

    this._changeWindowVisible(visible);
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getLegend() {
    return {
      id : MetarTaf.NAME,
      label: 'METAR/TAF',
    }
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getContentsVisibleCount() {
    return Object.values(this.contentsVisibleState).filter(state => state).length;
  }

  resetContentsVisibleState() {
    if (this.contentsVisibleCount === 0) {
      this.contentsVisibleState[MetarTaf.METAR_NAME] = true;
      this.contentsVisibleState[MetarTaf.TAF_NAME] = true;
      this._updateDataset();
      this.contentsVisibleCount = this.getContentsVisibleCount();
    }
  }

  getTimeInformation() {
    const timeList = [];
    Object.keys(this.contentsVisibleState).forEach(contents => {
      if (this.contentsVisibleState[contents]) {
        timeList.push({
          id: contents,
          type: contents,
          time: this.baseTime[contents]
        });
      }
    });

    return {
      id: MetarTaf.NAME,
      label: 'METAR/TAF',
      baseTime: timeList,
      baseTimeFormatter: (time) => {
        return `${translation(time.type)} ${time.time === '---' ? time.time : formatTimeZoneDate(time.time)}`
      }
    };
  }

  changeRoutePointEditing(routePointEditing) {
    this._allLayers().forEach(layer => {
      layer.disableInteraction(routePointEditing);
    });
  }

  getAirportGroup() {
    return this.airportGroup;
  }

  setAirportGroup(airportGroup) {
    // Contents Set対応。falseが指定された場合、airportGroupListの先頭を指定
    let _airportGroup = airportGroup;
    if (airportGroup === false && this.airportGroupList) {
      _airportGroup = this.airportGroupList[0];
    }
    this.airportGroup = _airportGroup;
    if (this.dh && this.getContentsVisibleCount() > 0) {
      this._updateDataset();
      this.dh.load('data', null, true);
      this._clearWindow();
    }
  }

  _updateDataset() {
    let content = [];
    Object.keys(this.contentsVisibleState).forEach(key => {
      if (this.contentsVisibleState[key]) {
        content.push(key.toLowerCase());
      }
    });
    var opt = {
      content: content.join(','),
      provider: 'noa,jma,faa'
    };
    if (this.airportGroup) {
      opt.airport_group = this.airportGroup;
    }
    Object.assign(this.dh.source.data.opt, opt);
  }

  _allLayers() {
    return [this.layerMetar, this.layerTaf];
  }

  _setBaseTime(ref) {
    let icaoHash = {};
    if (ref && ref.features) {
      ref.features.forEach(f => {
        icaoHash[f.properties.icao_code] = f.properties
      });
    }
    if (Object.keys(icaoHash).length === 0) return;

    let changed = false;
    [MetarTaf.METAR_NAME, MetarTaf.TAF_NAME].forEach(dataType => {
      const latest = Object.keys(icaoHash).reduce((latestTime, icao) => {
        const icaoData = icaoHash[icao][dataType];
        let latestProvider;
        if (icaoData) {
          latestProvider = Object.keys(icaoData).reduce((latestTimeProvider, provider) => {
            return latestTimeProvider < icaoData[provider].announce_time ? icaoData[provider].announce_time : latestTimeProvider;
          }, '');
        }
        return latestProvider && latestTime < latestProvider ? latestProvider : latestTime;
      }, '');

      if (latest !== '' && this.baseTime[dataType] !== latest) {
        this.baseTime[dataType] = latest;
        changed = true;
      }
    });

    if (changed) {
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  _toolTip(geo, data, type) {
    const p = geo && geo.properties;
    if (!p || !data) return;

    const latest = Object.keys(data).reduce((latest, provider) => {
      return latest < data[provider].announce_time ? data[provider].announce_time : latest;
    }, '');

    let _data = {};
    Object.keys(data).forEach(provider => {
      if (data[provider].announce_time === latest) {
        _data[provider] = data[provider];
      }
    });

    for (let provider of MetarTaf.PROVIDER_PRIORITY) {
      if (_data[provider]) {
        return `<pre>${type}: ${p.icao}\n ${_data[provider].bulletin}</pre>`;
      }
    }
  }

  /**
   * 欠測チェックのtimelistを生成
   */
  _makeTimelist(dataId, ref) {
    if (!this.contentsVisibleState[dataId]) {
      //METARやTAFがcheck offの場合はデータが入ってこないので欠測判定にならないように現在日付を設定しておく
      return [formatUTCDate(new Date())]
    }

    const timelist = new Set();//重複時制は除去
    ref.features.forEach(feature => {
      if (feature.properties[dataId] !== undefined) {
        Object.keys(feature.properties[dataId]).forEach(provider => {
          const time = feature.properties[dataId]?.[provider]?.announce_time;
          if (time) {
            timelist.add(time);
          }
        })
      }
    });
    return [...timelist];
  }

  _clickIcaoCode(event) {
    window.open(`${process.env.REACT_APP_PRO_POINT_SUMMARY_URL}?airport=${event.target.textContent}`, '_blank');
  }

  setRespondable(respondable) {
    this.layerMetar.setRespondable(respondable);
    this.layerTaf.setRespondable(respondable);
  }
}

const MISSING_THRESHOLD = {
  [MetarTaf.METAR_NAME]: 5400,
  [MetarTaf.TAF_NAME]: 25200
}

export default MetarTaf;
